import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Scale, TrendingUp, TrendingDown, FileDown, Wallet } from "lucide-react";
import { api } from "@/lib/apiClient";
import { money, fmtDate } from "./ClientesModule";
import { descargarPdf } from "./ReciboCobro";

const METODOS = { transferencia: "Transferencia", sinpe: "SINPE Móvil", efectivo: "Efectivo", "": "—" };
const mesActual = () => new Date().toISOString().slice(0, 7);
const mesAnterior = (m) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const fmtMes = (m) => {
  const s = new Date(m + "-02T00:00:00").toLocaleDateString("es-CR", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmtHoy = () => new Date().toLocaleDateString("es-CR", { day: "numeric", month: "short", year: "numeric" });

const totalesPorMoneda = (lista, campo) => {
  const t = { CRC: 0, USD: 0 };
  lista.forEach((x) => { t[x.currency === "USD" ? "USD" : "CRC"] += x[campo] || 0; });
  return t;
};
const strMonedas = (t) => {
  const parts = [t.CRC ? money(t.CRC, "CRC") : "", t.USD ? money(t.USD, "USD") : ""].filter(Boolean);
  if (!parts.length) return money(0, "CRC");
  return parts.join(parts.some((p) => p.startsWith("-")) ? "  ·  " : " + ");
};

function ResumenCard({ icon: Icon, label, value, note, tone, testid }) {
  return (
    <div className="rounded-xl bg-vnavy-800 border border-white/10 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${tone || "text-vteal-light"}`} />
        <span className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-semibold">{label}</span>
      </div>
      <p className="font-serif text-2xl text-white">{value}</p>
      {note && <p className="text-gray-500 text-xs mt-1">{note}</p>}
    </div>
  );
}

// Variación contra el mes anterior: porcentaje sobre colones (moneda principal) + delta por moneda
function variacionInfo(actual, prev) {
  const dCRC = actual.CRC - prev.CRC;
  const dUSD = actual.USD - prev.USD;
  const pct = prev.CRC > 0 ? Math.round((dCRC / prev.CRC) * 100) : (dCRC > 0 ? 100 : 0);
  const deltas = [dCRC ? `${dCRC > 0 ? "+" : ""}${money(dCRC, "CRC")}` : "", dUSD ? `${dUSD > 0 ? "+" : ""}${money(dUSD, "USD")}` : ""].filter(Boolean).join(" · ");
  return { pct, deltas, up: dCRC + dUSD >= 0 };
}

function NotaVariacion({ actual, prev, mesPrev, invertir = false }) {
  if (!prev.CRC && !prev.USD) return <>{fmtMes(mesPrev)}: sin movimientos</>;
  const v = variacionInfo(actual, prev);
  const bueno = invertir ? !v.up : v.up; // en gastos, subir es negativo
  return (
    <span className="flex items-center gap-1.5 flex-wrap">
      <span>{fmtMes(mesPrev)}: {strMonedas(prev)}</span>
      <span className={`inline-flex items-center gap-1 font-semibold ${bueno ? "text-green-400" : "text-red-400"}`}>
        {v.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        {v.pct > 0 ? "+" : ""}{v.pct}%{v.deltas ? ` (${v.deltas})` : ""}
      </span>
    </span>
  );
}

export default function BalanceModule() {
  const [month, setMonth] = useState(mesActual());
  const [cobros, setCobros] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [prevCobros, setPrevCobros] = useState([]);
  const [prevGastos, setPrevGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const mesPrev = mesAnterior(month);

  const load = useCallback(async () => {
    setLoading(true);
    const prev = mesAnterior(month);
    try {
      const [c, g, pc, pg] = await Promise.all([
        api.get(`/admin/cobros/registro?month=${month}`),
        api.get(`/admin/expenses?month=${month}`),
        api.get(`/admin/cobros/registro?month=${prev}`),
        api.get(`/admin/expenses?month=${prev}`),
      ]);
      setCobros((c.data || []).slice().sort((a, b) => a.fecha_cobro.localeCompare(b.fecha_cobro)));
      setGastos((g.data || []).slice().sort((a, b) => a.fecha.localeCompare(b.fecha)));
      setPrevCobros(pc.data || []);
      setPrevGastos(pg.data || []);
    } catch {
      toast.error("No se pudo cargar el balance del mes.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const totCobros = useMemo(() => totalesPorMoneda(cobros, "monto"), [cobros]);
  const totGastos = useMemo(() => totalesPorMoneda(gastos, "monto"), [gastos]);
  const totPrevCobros = useMemo(() => totalesPorMoneda(prevCobros, "monto"), [prevCobros]);
  const totPrevGastos = useMemo(() => totalesPorMoneda(prevGastos, "monto"), [prevGastos]);
  const balance = { CRC: totCobros.CRC - totGastos.CRC, USD: totCobros.USD - totGastos.USD };
  const balancePrev = { CRC: totPrevCobros.CRC - totPrevGastos.CRC, USD: totPrevCobros.USD - totPrevGastos.USD };

  const download = async () => {
    setDownloading(true);
    try {
      await descargarPdf("balance-doc", `Balance-${month}.pdf`);
      toast.success("PDF del balance descargado.");
    } catch {
      toast.error("No se pudo generar el PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const cobroDetalle = (c) =>
    [METODOS[c.metodo_pago || ""] || "—", c.quincena ? `${c.quincena === "1" ? "1ª" : "2ª"} quincena` : "", c.receipt_numero ? `N° ${c.receipt_numero}` : ""]
      .filter(Boolean).join(" · ");

  return (
    <div data-testid="balance-module">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
          data-testid="balance-month"
          className="h-9 rounded-md border border-white/15 bg-vnavy px-3 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-vteal-light/50" />
        <button onClick={download} disabled={downloading || loading} data-testid="balance-download"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vteal-light text-vnavy text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
          <FileDown className="h-4 w-4" /> {downloading ? "Generando PDF..." : "Descargar PDF"}
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <ResumenCard icon={TrendingUp} label={`Cobros · ${fmtMes(month)}`} value={strMonedas(totCobros)}
          note={<NotaVariacion actual={totCobros} prev={totPrevCobros} mesPrev={mesPrev} />} testid="balance-total-cobros" />
        <ResumenCard icon={TrendingDown} label={`Gastos · ${fmtMes(month)}`} value={strMonedas(totGastos)}
          note={<NotaVariacion actual={totGastos} prev={totPrevGastos} mesPrev={mesPrev} invertir />} tone="text-red-400" testid="balance-total-gastos" />
        <ResumenCard icon={Scale} label="Balance del mes" value={strMonedas(balance)}
          note={<NotaVariacion actual={balance} prev={balancePrev} mesPrev={mesPrev} />}
          tone={balance.CRC + balance.USD >= 0 ? "text-green-400" : "text-red-400"} testid="balance-total-neto" />
      </div>

      {loading ? (
        <p className="text-gray-500 py-12 text-center">Cargando balance...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          {/* Documento imprimible */}
          <div id="balance-doc" data-testid="balance-doc" className="relative bg-white text-slate-800 p-10 min-w-[760px] overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <img src="/assets/recurso-26.png" alt="" aria-hidden="true"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] opacity-[0.06] pointer-events-none select-none" />

            {/* Encabezado */}
            <div className="relative flex items-start justify-between gap-6 pb-6 border-b-2 border-[#0f2a43]">
              <img src="/assets/recurso-24.webp" alt="Grupo Vértice CR" className="h-16 w-auto" />
              <div className="text-right">
                <p className="text-[#0d9488] font-bold tracking-[0.2em] text-sm uppercase">Balance Mensual</p>
                <p className="text-slate-500 text-xs mt-1">{fmtMes(month)}</p>
                <p className="text-slate-500 text-xs">Generado: {fmtHoy()}</p>
              </div>
            </div>

            {/* Cobros */}
            <div className="relative mt-8">
              <p className="text-[#0f2a43] font-bold text-sm uppercase tracking-[0.15em] mb-3">Cobros realizados</p>
              {cobros.length === 0 ? (
                <p className="text-slate-400 text-sm py-3">No hubo cobros registrados este mes.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-slate-200">
                      <th className="py-2 pr-3 font-semibold">Fecha</th>
                      <th className="py-2 pr-3 font-semibold">Empresa</th>
                      <th className="py-2 pr-3 font-semibold">Detalle</th>
                      <th className="py-2 font-semibold text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cobros.map((c) => (
                      <tr key={c.id} className="border-b border-slate-100">
                        <td className="py-2.5 pr-3 text-slate-500 whitespace-nowrap">{fmtDate(c.fecha_cobro)}</td>
                        <td className="py-2.5 pr-3 font-medium">{c.company || "Cliente"}</td>
                        <td className="py-2.5 pr-3 text-slate-500 text-xs">{cobroDetalle(c)}</td>
                        <td className="py-2.5 text-right font-semibold whitespace-nowrap">{money(c.monto, c.currency)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50">
                      <td colSpan={3} className="py-2.5 pr-3 font-bold text-[#0f2a43]">Total cobros</td>
                      <td className="py-2.5 text-right font-bold text-[#0d9488] whitespace-nowrap" data-testid="balance-doc-total-cobros">{strMonedas(totCobros)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* Gastos */}
            <div className="relative mt-8">
              <p className="text-[#0f2a43] font-bold text-sm uppercase tracking-[0.15em] mb-3">Gastos registrados</p>
              {gastos.length === 0 ? (
                <p className="text-slate-400 text-sm py-3">No hubo gastos registrados este mes.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-slate-200">
                      <th className="py-2 pr-3 font-semibold">Fecha</th>
                      <th className="py-2 pr-3 font-semibold">Concepto</th>
                      <th className="py-2 pr-3 font-semibold">Categoría</th>
                      <th className="py-2 font-semibold text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gastos.map((g) => (
                      <tr key={g.id} className="border-b border-slate-100">
                        <td className="py-2.5 pr-3 text-slate-500 whitespace-nowrap">{fmtDate(g.fecha)}</td>
                        <td className="py-2.5 pr-3 font-medium">{g.concepto}</td>
                        <td className="py-2.5 pr-3 text-slate-500 text-xs">{g.categoria || "Otros"}</td>
                        <td className="py-2.5 text-right font-semibold whitespace-nowrap">{money(g.monto, g.currency)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50">
                      <td colSpan={3} className="py-2.5 pr-3 font-bold text-[#0f2a43]">Total gastos</td>
                      <td className="py-2.5 text-right font-bold text-red-600 whitespace-nowrap" data-testid="balance-doc-total-gastos">{strMonedas(totGastos)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* Balance */}
            <div className="relative mt-8 rounded-xl border-2 border-[#0f2a43] p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Balance del mes · {fmtMes(month)}</p>
                <p className="text-slate-500 text-xs mt-1">Cobros menos gastos</p>
              </div>
              <p className={`text-2xl font-bold whitespace-nowrap ${balance.CRC + balance.USD >= 0 ? "text-[#0d9488]" : "text-red-600"}`} data-testid="balance-doc-neto">
                {strMonedas(balance)}
              </p>
            </div>

            {/* Comparación con el mes anterior */}
            <div className="relative mt-8">
              <p className="text-[#0f2a43] font-bold text-sm uppercase tracking-[0.15em] mb-3">Comparación con {fmtMes(mesPrev)}</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-slate-200">
                    <th className="py-2 pr-3 font-semibold">Concepto</th>
                    <th className="py-2 pr-3 font-semibold text-right">{fmtMes(mesPrev)}</th>
                    <th className="py-2 pr-3 font-semibold text-right">{fmtMes(month)}</th>
                    <th className="py-2 font-semibold text-right">Variación</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Cobros", actual: totCobros, prev: totPrevCobros, invertir: false, testid: "balance-doc-cmp-cobros" },
                    { label: "Gastos", actual: totGastos, prev: totPrevGastos, invertir: true, testid: "balance-doc-cmp-gastos" },
                    { label: "Balance", actual: balance, prev: balancePrev, invertir: false, testid: "balance-doc-cmp-neto" },
                  ].map((row) => {
                    const v = variacionInfo(row.actual, row.prev);
                    const bueno = row.invertir ? !v.up : v.up;
                    return (
                      <tr key={row.label} className="border-b border-slate-100" data-testid={row.testid}>
                        <td className="py-2.5 pr-3 font-medium">{row.label}</td>
                        <td className="py-2.5 pr-3 text-right text-slate-500 whitespace-nowrap">{strMonedas(row.prev)}</td>
                        <td className="py-2.5 pr-3 text-right font-semibold whitespace-nowrap">{strMonedas(row.actual)}</td>
                        <td className={`py-2.5 text-right font-bold whitespace-nowrap ${bueno ? "text-[#0d9488]" : "text-red-600"}`}>
                          {v.up ? "▲" : "▼"} {v.pct > 0 ? "+" : ""}{v.pct}%{v.deltas ? ` · ${v.deltas}` : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pie */}
            <div className="relative mt-8 pt-4 border-t border-slate-200 text-slate-500 text-xs leading-relaxed">
              <p>Emisor: Grupo Vértice CR · Cédula jurídica: 3-102-939708 · Sociedad de Responsabilidad Limitada</p>
              <p>Puntarenas, Esparza, Espíritu Santo, 175 metros este del MAG, Edificio Enlace de Negocios</p>
              <p>Teléfono: 8596-7408</p>
            </div>
          </div>
        </div>
      )}

      {!loading && cobros.length === 0 && gastos.length === 0 && (
        <div className="text-center py-10 text-gray-500">
          <Wallet className="h-10 w-10 mx-auto mb-3 opacity-40" />
          No hay movimientos en {fmtMes(month)}. Registre cobros y gastos para ver el balance.
        </div>
      )}
    </div>
  );
}
